import React, { useEffect, useState } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, NavLink, useNavigate, useParams, useLocation } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { confirmAppointmentRescheduleRequest, getClientAppointments, rateMeeting, getClientAppointmentsByParticularAdvisor, checkBlockedStatus, updateAppointmentStatus, getCompletedAppointmentsByClientId, getNotes, saveNotes } from "../shared/_services/common.services";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../shared/_helpers/apiErrors";
import moment from 'moment';
import { useSnackbar } from "notistack";
import Loader from "./Loader";
import DashboardUserHeader from "../components/dashboard-flow/DashboardUserHeader";
import { Editor } from 'primereact/editor';
import { Button } from 'primereact/button';
import 'primereact/resources/themes/lara-light-indigo/theme.css'; // or any other theme
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import meetingPlaceholder from "../assets/images/No Meetings.png"


const PRIVILEGED_USER_TYPES = ["advisor", "super-admin", "admin"];
const CLIENT_NOTE_USER_TYPES = ["client", "super-admin", "admin"];

function isPrivilegedUserType(userType) {
  return PRIVILEGED_USER_TYPES.includes(userType);
}

function canOpenNotes(userType) {
  return CLIENT_NOTE_USER_TYPES.includes(userType);
}

function getAdvisorInitials(advisor) {
  const firstInitial = advisor?.firstName?.[0]?.toUpperCase() || "";
  const lastInitial = advisor?.lastName?.[0]?.toUpperCase() || "";
  return `${firstInitial}${lastInitial}`;
}

function getMeetingTimeRange(time, duration) {
  if (!time || !duration) return "";
  const start = moment(time, "h:mm A").format("h:mm A");
  const end = moment(time, "h:mm A").add(duration, "minutes").format("h:mm A");
  return `${start} - ${end}`;
}

function getMeetingDayLabel(date) {
  return moment.utc(date).format("dddd MMM Do");
}

function shouldShowMeetingDateHeader(currentDate, previousDate) {
  return currentDate !== previousDate;
}

function MeetingRatingSection({
  meeting,
  userType,
  isReadMore,
  onToggleReadMore,
  onRateClick,
}) {
  if (meeting?.appointment_status !== "completed") {
    return <div className="col-2 place-content-center"></div>;
  }

  const isPrivileged = isPrivilegedUserType(userType);
  const ratingComment = meeting?.rating_comment || "";
  const hasLongComment = ratingComment.length > 30;

  return (
    <div className="col-2 place-content-center">
      <div>
        <div className="ratings-stars">
          {[1, 2, 3, 4, 5].map((star) => (
            <i
              key={star}
              className={`iconoir-${star <= (meeting?.rating || 0) ? "star-solid" : "star"}`}
            ></i>
          ))}
        </div>
        {!isPrivileged && (
          <Link
            onClick={() => onRateClick(meeting)}
            className="cursor-pointer small-txt"
          >
            Rate this meeting
          </Link>
        )}
        {isPrivileged && (
          <p className="pt-2">
            {hasLongComment ? (
              <>
                <span>{isReadMore ? ratingComment : `${ratingComment.slice(0, 15)}...`}</span>
                <span
                  onClick={onToggleReadMore}
                  className="cursor-pointer"
                  style={{ color: "#63363A", paddingLeft: "3px" }}
                >
                  {isReadMore ? "Read Less" : "Read More"}
                </span>
              </>
            ) : (
              <span>{ratingComment}</span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

function MeetingPrimaryAction({ meeting, onJoinMeeting }) {
  if (meeting?.appointment_status === "completed") {
    return (
      <NavLink
        to={{ pathname: "/meeting-details" }}
        state={{
          id: meeting._id,
          date: meeting.date,
          time: meeting.time,
          duration: meeting.duration,
          userId: meeting.clientId,
        }}
        className="as-btn-primary meeting-btn f-600"
      >
        Details
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      className="as-btn-primary meeting-btn"
      onClick={() => onJoinMeeting(meeting)}
    >
      <svg
        className="mr-2"
        width="21"
        height="14"
        viewBox="0 0 21 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 10.75V3.25C1.5 2.71957 1.71071 2.21086 2.08579 1.83579C2.46086 1.46071 2.96957 1.25 3.5 1.25H12C12.5304 1.25 13.0391 1.46071 13.4142 1.83579C13.7893 2.21086 14 2.71957 14 3.25V10.75C14 11.2804 13.7893 11.7891 13.4142 12.1642C13.0391 12.5393 12.5304 12.75 12 12.75H3.5C2.96957 12.75 2.46086 12.5393 2.08579 12.1642C1.71071 11.7891 1.5 11.2804 1.5 10.75ZM18.668 1.991L14.668 5.554C14.6153 5.60081 14.5731 5.65825 14.5441 5.72253C14.5152 5.78682 14.5001 5.8565 14.5 5.927V7.705C14.5001 7.7755 14.5152 7.84518 14.5441 7.90947C14.5731 7.97375 14.6153 8.03119 14.668 8.078L18.668 11.641C18.7401 11.705 18.8291 11.7468 18.9244 11.7614C19.0196 11.776 19.1171 11.7627 19.205 11.7232C19.2929 11.6837 19.3675 11.6196 19.4199 11.5386C19.4722 11.4577 19.5 11.3634 19.5 11.267V2.365C19.5 2.26862 19.4722 2.17429 19.4199 2.09335C19.3675 2.01242 19.2929 1.94834 19.205 1.90882C19.1171 1.86931 19.0196 1.85604 18.9244 1.87062C18.8291 1.8852 18.7401 1.927 18.668 1.991Z"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Join
    </button>
  );
}

function MeetingRescheduleRequest({
  meeting,
  onConfirmReschedule,
}) {
  if (!(meeting?.reschedule_request && meeting.appointment_status === "pending")) {
    return null;
  }

  return (
    <div className="reschedule-req-box d-flex justify-content-between">
      <div className="d-flex place-items-center">
        <i className="iconoir-warning-circle"></i>
        <p className="text-dark ml-2">
          Your financial Advisor has requested to change the meeting
          to <b>{getMeetingDayLabel(meeting?.date)}</b> at <b>{moment.utc(meeting.time, "HH:mm").format("h:mm A")} - {moment.utc(meeting.time, "HH:mm").add(30, "minute").format("h:mm A")}</b>{" "}
        </p>
      </div>
      <div className="d-flex">
        <button className="meeting-req-btn mr-2" onClick={() => onConfirmReschedule(meeting?._id, 0)}>
          <svg
            className="mr-2"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.875 1.125L1.125 10.875M1.125 1.125L10.875 10.875"
              stroke="#FF0000"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Deny
        </button>
        <button className="meeting-req-btn" onClick={() => onConfirmReschedule(meeting?._id, 1)}>
          <svg
            className="mr-2"
            width="14"
            height="11"
            viewBox="0 0 14 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 1.128L4.4 10.728L0 6.328L1.128 5.2L4.4 8.464L12.872 0L14 1.128Z"
              fill="#00FF0A"
            />
          </svg>
          Accept
        </button>
      </div>
    </div>
  );
}

function MeetingCard({
  meeting,
  previousMeetingDate,
  userType,
  openingNotesId,
  isReadMore,
  onToggleReadMore,
  onOpenEditorWithDelay,
  onJoinMeeting,
  onRateClick,
  onConfirmReschedule,
}) {
  const currentMeetingDate = moment(meeting?.date).format("YYYY-MM-DD");
  const showDateHeader = shouldShowMeetingDateHeader(currentMeetingDate, previousMeetingDate);
  const advisorHasImage = Boolean(meeting?.advisor?.profile_image);

  return (
    <div className="meeting-single mb-4">
      {showDateHeader && <h6 className="f-600 mb-2">{getMeetingDayLabel(meeting?.date)}</h6>}
      <div className="meeting-dtl-box">
        <div className="row">
          <div className="col-3 d-flex">
            <div className="profile-pic">
              {advisorHasImage ? (
                <img src={meeting?.advisor?.profile_image} alt="" />
              ) : (
                <h3>{getAdvisorInitials(meeting?.advisor)}</h3>
              )}
            </div>
            <div className="ml-2">
              <p className="meeting-time">{getMeetingTimeRange(meeting?.time, meeting?.duration)}</p>
              <p className={`meeting-status ${meeting?.appointment_status}`}>{meeting?.reschedule_request ? meeting?.appointment_status : ""}</p>
            </div>
          </div>
          <div className="col-2">
            <p className="advisor-name mb-0 f-700 text-dark">{meeting?.advisor?.firstName} {meeting?.advisor?.lastName}</p>
            <p className="small-txt">Financial Advisor</p>
          </div>

          <MeetingRatingSection
            meeting={meeting}
            userType={userType}
            isReadMore={isReadMore}
            onToggleReadMore={onToggleReadMore}
            onRateClick={onRateClick}
          />

          <div className="col-5 place-items-center d-flex justify-content-end gap-20">
            {canOpenNotes(userType) && (
              <Button
                label={openingNotesId === meeting._id ? "Loading..." : "Notes"}
                icon="pi pi-pencil"
                className="as-btn-secondary meeting-btn small-icon"
                style={{ fontSize: "14px" }}
                onClick={() => onOpenEditorWithDelay(meeting._id)}
                disabled={openingNotesId === meeting._id}
              />
            )}

            <MeetingPrimaryAction
              meeting={meeting}
              onJoinMeeting={onJoinMeeting}
            />
          </div>
        </div>
        <MeetingRescheduleRequest
          meeting={meeting}
          onConfirmReschedule={onConfirmReschedule}
        />
      </div>
    </div>
  );
}

const MeetingList = () => {
  const [reviewPopupShow, setReviewPopupShow] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pathname = queryParams.get('isadmin');
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();
  const [rating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState('');
  const [appointmentId, setAppointmentId] = useState('');
  const [isReadMore, setIsReadMore] = useState(false);
  const [editorPopupShow, setEditorPopupShow] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [notes, setNotes] = useState({});
  
  const [openingNotesId, setOpeningNotesId] = useState(null);


  const userType = JSON.parse(localStorage.getItem('currentUser'))?.user?.userType;

  const getAppointments = async () => {
    setShowLoader(true)
    let data;
    if (params?.clientId && pathname) {
      data = await getCompletedAppointmentsByClientId(params?.clientId);
    } else if (params?.clientId) {
      data = await getClientAppointmentsByParticularAdvisor({
        clientId: params?.clientId,
        advisorId: JSON.parse(localStorage.getItem('currentUser'))?.user?._id,
      });
    } else {
      data = await getClientAppointments(
        JSON.parse(localStorage.getItem('currentUser'))?.user?._id
      );
    }
    if (data?.status === 200) {
      if (data?.data?.length > 0) {
        setAppointments([...data.data])
      }
    }
    else {
      if (data?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired!", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      } else if (isForbiddenClientAccess(data)) {
        enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate(CLIENT_ACCESS_DENIED_REDIRECT);
      }
    }
    setShowLoader(false)
  }
  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);
      if (response.data ? response.data.status : response.response.status === 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;
        if (isBlocked || isDeleted) {
          clearSession();
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
    }
  }
  
  
  // Function to check and update past appointments
  const checkAndUpdateAppointments = async () => {
  const pastAppointments = Array.isArray(appointments)
    ? appointments.filter((appt) => {
        if (!appt?.date || !appt?.time || !appt?.timezone || !appt?.duration) {
          return false;
        }
        // Extract date portion from ISO string
        const localDate = appt.date.split("T")[0]; // e.g., "2025-10-15"
       // Parse start time in the correct timezone
        const startTime = moment.tz(
          `${localDate} ${appt.time}`,
          "YYYY-MM-DD h:mm A",
          appt.timezone
        );
        // Calculate end time by adding duration
       const endTime = startTime.clone().add(appt.duration, "minutes");
        // Check if current time is after end time
        const hasEnded = moment().isAfter(endTime);
        // Only consider appointments that are still marked as "upcoming"
        const isUpcoming = appt.appointment_status === "upcoming";
  
        return hasEnded && isUpcoming;
      })
    : [];

  if (pastAppointments.length > 0) {
    for (const appointment of pastAppointments) {
      try {
        const res = await updateAppointmentStatus(appointment._id, "completed");
        if (res.status === 200) {
          await getAppointments();
        }
      } catch (error) {
      }
    }
  }
};

  useEffect(() => {
    checkBlocked();
    getAppointments();
    checkAndUpdateAppointments();
  }, []); //eslint-disable-line

  const handleJoinMeeting = async (data) => {
    window.open(data.join_url, '_blank');
  };

  const handleConfirmReschedule = async (id, status) => {
    setShowLoader(true)
    let data = await confirmAppointmentRescheduleRequest({ appointmentId: id, status });

    if (data.status === 200) {
      const res = await getClientAppointments(JSON.parse(localStorage.getItem('currentUser'))?.user?._id);
      if (res) {
        setAppointments(res.data);
        setShowLoader(false)
      }
      enqueueSnackbar(data?.data?.message, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  }


  const handleRateClick = (data) => {
    setReviewPopupShow(true)
    setAppointmentId(data?._id);
    setSelectedRating(data?.rating);
    setComment(data?.rating_comment);
  }

  const handleStarClick = (index) => {
    setSelectedRating(index + 1);
  };

  const handleSubmit = async () => {
    if (rating === 0 && !comment.trim()) {
      return enqueueSnackbar('Please select a rating and add a comment.', { variant: 'warning' });
    }
    const response = await rateMeeting({
      appointmentId,
      rating,
      comment,
    });

    if (response) {
      enqueueSnackbar('Rating submitted successfully.', { variant: 'success' });
      setReviewPopupShow(false);
      getAppointments();
    } else {
      enqueueSnackbar('Failed to submit rating. Please try again.', { variant: 'error' });
    }
  };

const handleOpenEditorWithDelay = (id) => {
  setOpeningNotesId(id);
  setTimeout(() => {
    handleOpenEditor(id); 
  }, 300); 
};

const handleOpenEditor = async (appointmentId) => {
  try {
    const response = await getNotes(appointmentId, "client");
    if (response.status === 200) {
      setNotes({ ...notes, [appointmentId]: response.data.data.notes || '' });
      setActiveNoteId(appointmentId);
      setEditorPopupShow(true);
    } else {
      enqueueSnackbar(response.data?.message || "Failed to retrieve notes.", { variant: 'error' });
    }
  } catch (error) {
    console.error("Error retrieving notes:", error);
    enqueueSnackbar("Error retrieving notes.", { variant: 'error' });
  } finally {
    setOpeningNotesId(null); // Clear the loading state
  }
};

  const handleSaveNotes = async (appointmentId) => {
    try {
      const response = await saveNotes(appointmentId, notes[appointmentId], "client");
      if (response.status === 200) {
        enqueueSnackbar("Notes saved!", { variant: 'success' });
        setEditorPopupShow(false);
      } else {
        enqueueSnackbar(response.data?.message || "Failed to save notes.", { variant: 'error' });
      }
    } catch (error) {
      console.error("Error saving notes:", error);
      enqueueSnackbar("Error saving notes.", { variant: 'error' });
    }
  };

  checkAndUpdateAppointments();

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
  };

  const [selectedFilter, setSelectedFilter] = useState("All");

  // Filter logic
  const filteredAppointments = appointments?.filter((appt) => {
    if (selectedFilter === "All") return true;
    if (selectedFilter === "Upcoming") return appt.appointment_status === "upcoming";
    if (selectedFilter === "Pending") return appt.appointment_status === "pending";
    if (selectedFilter === "Completed") return appt.appointment_status === "completed";
    return true;
  });

  return (
    <>
      {(JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'advisor' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'super-admin' ||
        JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'admin') &&
        <DashboardUserHeader clientId={params?.clientId ? params?.clientId : null} />}

      {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'client' &&
        <div className="page-header">
          <h2>Meetings</h2>
          <p>Your complete log of past, present, and upcoming meetings.</p>
        </div>}

      {showLoader ? <Loader /> :
        <div className="meetings-body">
          <div className="user-meetings-filter">
            {["All", "Upcoming", "Pending","Completed"].map((filter) => (
              <div
                key={filter}
                className={`user-meetings-filter-single ${selectedFilter === filter ? "if_selected" : ""
                  }`}
                onClick={() => setSelectedFilter(filter)}
              >
                {filter}
              </div>
            ))}
          </div>
          {filteredAppointments?.length > 0 ? filteredAppointments?.map((data, index) => {
            const previousMeetingDate = index > 0 ? moment(filteredAppointments[index - 1]?.date).format("YYYY-MM-DD") : null;
            return (
              <MeetingCard
                key={data?._id}
                meeting={data}
                previousMeetingDate={previousMeetingDate}
                userType={userType}
                openingNotesId={openingNotesId}
                isReadMore={isReadMore}
                onToggleReadMore={toggleReadMore}
                onOpenEditorWithDelay={handleOpenEditorWithDelay}
                onJoinMeeting={handleJoinMeeting}
                onRateClick={handleRateClick}
                onConfirmReschedule={handleConfirmReschedule}
              />
            );
          }) : (
            <>
            <div style={{ textAlign: 'center' }}>
              <img src={meetingPlaceholder} alt="no meeting"/>
            </div>
            </>
          )}
        </div>}

      {/* thank you popup */}
      <Modal
        size="md"
        className="reviewPopup"
        show={reviewPopupShow}
        onHide={() => setReviewPopupShow(false)}
        aria-labelledby="reviewPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <form>
            <div className="d-flex justify-content-end">
              <Link
                className="close-popup-btn"
                onClick={() => setReviewPopupShow(false)}
              >
                <i className="iconoir-xmark"></i>
              </Link>
            </div>
            <div className="ratings-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`iconoir-${star <= rating ? 'star-solid' : 'star'}`}
                  onClick={() => handleStarClick(star - 1)}
                  style={{ cursor: 'pointer' }}
                ></i>
              ))}
            </div>
            <h4 className="text-center f-700 text-astra">Thank you for you rating.</h4>
            <p className="mb-4 f-400 text-grey text-center">Let us know how we can improve!</p>
            <textarea
              className="cust-textarea"
              placeholder="Share your thoughts here."
              id="review"
              name="review"
              rows="5"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            ></textarea>
            <div className="text-center mt-4">
              <button type="button" className="as-btn-primary place-items-center d-flex mx-auto f-500 px-4 py-2" onClick={handleSubmit}
              >
                <i className="iconoir-check" style={{ marginRight: '5px', fontSize: '20px' }}></i>
                Submit Review
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Editor Popup */}
      <Modal
        size="lg"
        className="editorPopup"
        show={editorPopupShow}
        onHide={() => setEditorPopupShow(false)}
        aria-labelledby="editorPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-end">
            <Link
              className="close-popup-btn"
              onClick={() => setEditorPopupShow(false)}
            >
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="text-center">Meeting Notes</h4>
          <Editor
            value={notes[activeNoteId] || ''}
            onTextChange={(e) =>
              setNotes({ ...notes, [activeNoteId]: e.htmlValue })
            }
            style={{ height: '300px' }}
          />
          <div className="text-center mt-4">
            <Button
              label="Save Notes"
              icon="pi pi-save"
              className="as-btn-primary meeting-btn small-icon"
              onClick={() => handleSaveNotes(activeNoteId)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MeetingList;
